<template>
  <section
    id="apply"
    class="bg-[#1A1A1A] text-white"
  >
    <!-- CTA-led footer hero -->
    <div class="px-5 pb-16 pt-14 text-center tablet:px-10 tablet:pb-20 tablet:pt-20">
      <p class="text-sm text-white/85">{{ t('landing.footer.eyebrow') }}</p>
      <h2 class="mt-4 text-[22px] font-bold leading-[1.25] tablet:text-[30px]">
        {{ t(isUae ? 'landing.uae.footer.title' : 'landing.footer.title') }}
      </h2>
      <p class="mx-auto mt-4 max-w-[520px] text-sm leading-[1.6] text-white/70">
        {{ t(isUae ? 'landing.uae.footer.description' : 'landing.footer.description') }}
      </p>

      <div class="mt-10">
        <NuxtLink
          :to="applyHref"
          @click="trackCtaClick('final')"
          class="mx-auto inline-flex h-14 w-full max-w-[506px] items-center justify-center gap-2 rounded-xl bg-gradient-to-b from-gold-primary-100 to-gold-primary-120 px-7 text-base font-bold text-black-100 shadow-[0_2px_0_rgba(0,0,0,0.04),0_8px_20px_rgba(237,164,54,0.25)] transition-transform hover:-translate-y-px"
        >
          <span
            >{{ t('landing.shared.applyToJoin') }}
            <span
              class="inline-block rtl:-scale-x-100"
              aria-hidden="true"
              >→</span
            ></span
          >
        </NuxtLink>
      </div>

      <!-- Brand lockup: I/ mark + wordmark + agents-network eyebrow -->
      <div class="mt-16 flex items-center justify-center gap-3">
        <UILogo
          variant="mark"
          :size="48"
          :link="false"
        />
        <div class="text-start">
          <UILogo
            theme="dark"
            :size="22"
            :link="false"
            variant="compact"
          />
          <div class="mt-1 text-[11px] uppercase tracking-[0.15em] text-white/60">
            {{ t('landing.footer.agentsNetwork') }}
          </div>
        </div>
      </div>

      <!-- Social row -->
      <div
        class="mt-8 flex justify-center gap-3"
        :aria-label="t('landing.footer.socialLinks')"
      >
        <a
          v-for="s in socials"
          :key="s.label"
          :href="s.href"
          :aria-label="s.label"
          :title="s.label"
          target="_blank"
          rel="noopener noreferrer"
          class="grid size-10 place-items-center rounded-[10px] border border-white/[0.08] bg-white/[0.04] text-white/70 transition-all duration-200 hover:-translate-y-px hover:border-gold-primary-100/40 hover:bg-gold-primary-100/[0.08] hover:text-gold-primary-100"
        >
          <svg
            viewBox="0 0 24 24"
            class="size-[18px] fill-current"
            aria-hidden="true"
            v-html="s.svg"
          ></svg>
        </a>
      </div>
    </div>

    <hr class="border-t border-gold-primary-100/50" />

    <!-- Links / copyright row -->
    <div
      class="flex flex-col items-center gap-3 px-6 py-5 text-xs text-white/70 tablet:flex-row tablet:justify-between tablet:px-10 tablet:py-7"
    >
      <p
        class="font-semibold"
        dir="ltr"
      >
        {{ t('landing.footer.copyright', { year: currentYear }) }}
      </p>
      <div class="flex flex-col flex-wrap items-center justify-center gap-1.5 tablet:flex-row tablet:gap-2.5">
        <a
          href="/contact"
          class="hover:text-white"
          >{{ t('landing.footer.contact') }}</a
        >
        <span class="hidden text-white/30 tablet:inline">·</span>
        <a
          href="/privacy"
          class="hover:text-white"
          >{{ t('landing.footer.privacy') }}</a
        >
        <span class="hidden text-white/30 tablet:inline">·</span>
        <a
          href="/terms"
          class="hover:text-white"
          >{{ t('landing.footer.terms') }}</a
        >
        <span class="hidden text-white/30 tablet:inline">·</span>
        <a
          href="/delete-account"
          class="hover:text-white"
          >{{ t('shell.legal.deleteAccount') }}</a
        >
        <LocaleMenu
          v-if="!isUae"
          tone="dark"
          placement="top"
          class="tablet:ms-2"
        />
      </div>
    </div>

    <hr class="border-t border-gold-primary-100/50" />

    <!-- Legal / business address row -->
    <div class="flex flex-col items-center gap-0.5 px-5 pb-11 pt-6 text-center text-[13px] text-white/70">
      <p dir="ltr">{{ t('landing.footer.businessName') }}</p>
      <p>{{ t('landing.footer.address') }}</p>
      <i18n-t
        keypath="landing.footer.geoipAttribution"
        tag="p"
        class="mt-2 text-[11px] text-white/50"
      >
        <template #provider>
          <a
            :href="GEOIP_ATTRIBUTION.url"
            target="_blank"
            rel="noopener"
            class="underline hover:text-white/70"
            >{{ GEOIP_ATTRIBUTION.provider }}</a
          >
        </template>
      </i18n-t>
      <a
        :href="crossMarketLink.href"
        class="mt-3 text-[11px] text-white/50 hover:text-white/70"
        >{{ t(crossMarketLink.labelKey) }}
        <span
          class="inline-block rtl:-scale-x-100"
          aria-hidden="true"
          >→</span
        ></a
      >
    </div>
  </section>
</template>

<script setup lang="ts">
import { crossMarketFooterLink } from '~/utils/landing/landingPage';
import { GEOIP_ATTRIBUTION } from '~/constants/geoip';

const { t } = useI18n();

type SocialLink = {
  label: string;
  href: string;
  svg: string;
};

const socials: SocialLink[] = [
  {
    label: 'LinkedIn',
    href: 'https://www.linkedin.com/company/insidersofdubai/',
    svg: '<path d="M20.45 20.45h-3.55v-5.57c0-1.33-.03-3.04-1.85-3.04-1.86 0-2.14 1.45-2.14 2.95v5.66H9.36V9h3.41v1.56h.05c.47-.9 1.63-1.85 3.36-1.85 3.6 0 4.27 2.37 4.27 5.45v6.29zM5.34 7.43a2.06 2.06 0 1 1 0-4.12 2.06 2.06 0 0 1 0 4.12zM7.12 20.45H3.56V9h3.56v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.72v20.56C0 23.23.79 24 1.77 24h20.45C23.2 24 24 23.23 24 22.28V1.72C24 .77 23.2 0 22.22 0z"/>',
  },
  {
    label: 'Instagram',
    href: 'https://www.instagram.com/insidersofdubai/',
    svg: '<path d="M12 2.16c3.2 0 3.58.01 4.85.07 1.17.05 1.8.25 2.23.41.56.22.96.48 1.38.9.42.42.68.82.9 1.38.16.42.36 1.06.41 2.23.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.05 1.17-.25 1.8-.41 2.23-.22.56-.48.96-.9 1.38-.42.42-.82.68-1.38.9-.43.16-1.06.36-2.23.41-1.27.06-1.65.07-4.85.07s-3.58-.01-4.85-.07c-1.17-.05-1.8-.25-2.23-.41a3.73 3.73 0 0 1-1.38-.9 3.73 3.73 0 0 1-.9-1.38c-.16-.43-.36-1.06-.41-2.23C2.17 15.58 2.16 15.2 2.16 12s.01-3.58.07-4.85c.05-1.17.25-1.8.41-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.43-.16 1.06-.36 2.23-.41C8.42 2.17 8.8 2.16 12 2.16zM12 0C8.74 0 8.33.01 7.05.07 5.78.13 4.9.33 4.14.63a5.9 5.9 0 0 0-2.13 1.38A5.9 5.9 0 0 0 .63 4.14C.33 4.9.13 5.78.07 7.05.01 8.33 0 8.74 0 12s.01 3.67.07 4.95c.06 1.27.26 2.15.56 2.91.31.79.73 1.46 1.38 2.13.67.65 1.34 1.07 2.13 1.38.76.3 1.64.5 2.91.56C8.33 23.99 8.74 24 12 24s3.67-.01 4.95-.07c1.27-.06 2.15-.26 2.91-.56a5.9 5.9 0 0 0 2.13-1.38 5.9 5.9 0 0 0 1.38-2.13c.3-.76.5-1.64.56-2.91.06-1.28.07-1.69.07-4.95s-.01-3.67-.07-4.95c-.06-1.27-.26-2.15-.56-2.91a5.9 5.9 0 0 0-1.38-2.13A5.9 5.9 0 0 0 19.86.63C19.1.33 18.22.13 16.95.07 15.67.01 15.26 0 12 0zm0 5.84a6.16 6.16 0 1 0 0 12.32 6.16 6.16 0 0 0 0-12.32zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm7.85-10.4a1.44 1.44 0 1 1-2.88 0 1.44 1.44 0 0 1 2.88 0z"/>',
  },
  {
    label: 'YouTube',
    href: 'https://www.youtube.com/@InsidersofDubai',
    svg: '<path d="M23.5 6.2a3 3 0 0 0-2.12-2.13C19.5 3.55 12 3.55 12 3.55s-7.5 0-9.38.52A3 3 0 0 0 .5 6.2C0 8.08 0 12 0 12s0 3.92.5 5.8a3 3 0 0 0 2.12 2.13c1.88.52 9.38.52 9.38.52s7.5 0 9.38-.52A3 3 0 0 0 23.5 17.8c.5-1.88.5-5.8.5-5.8s0-3.92-.5-5.8zM9.6 15.6V8.4l6.25 3.6-6.25 3.6z"/>',
  },
  {
    label: 'X (Twitter)',
    href: 'https://x.com/insidersofdubai',
    svg: '<path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77z"/>',
  },
];

const currentYear = new Date().getFullYear();
const { track } = useLandingPage();
// "We just pay it faster" is not true before the UAE cap, so /uae has its own closing copy.
const isUae = computed(() => track.value === 'uae');
// Deliberately quiet link to the other market's page (no toggle, no banner).
const crossMarketLink = computed(() => crossMarketFooterLink(track.value));

const { signupHref: applyHref } = useSignupHref();
const { trackCtaClick } = useLandingTracking();
</script>
